import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useUser } from '@/contexts/UserContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useRoles } from '@/contexts/RolesContext';
import { useTheme } from '@/components/theme-provider';
import { useMsal } from '@azure/msal-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { LoadingButton } from '@/components/LoadingButton';
import { OrgSettingsCard } from '@/components/settings/OrgSettingsCard';
import { FeatureFlagsSettingsCard } from '@/components/settings/FeatureFlagsSettingsCard';
import { UserEmailNotificationSettingsCard } from '@/components/settings/UserEmailNotificationSettingsCard';
import { LegalDocumentsSettingsCard } from '@/components/settings/LegalDocumentsSettingsCard';
import { PageHeader } from '@/components/common/PageHeader';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useUpdateExternalUserProfile } from '@/api/auth/self/self';
import { toast } from 'sonner';
import { LANGUAGES } from '@/constants/languages';
import type { Language } from '@/i18n/types';

/**
 * Settings page where users can update their profile information
 */
export default function SettingsPage() {
  const { instance } = useMsal();
  const { account, userProfile, updateUserProfile, clearUserProfile, updateUserSettings, isUpdatingSettings } = useUser();
  const [searchParams] = useSearchParams();
  const showFeatureFlags = searchParams.get('feature_flags') === 'true';
  const { t, language } = useLanguage();
  const { theme } = useTheme();
  const { isAdmin } = useRoles();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  const updateProfileMutation = useUpdateExternalUserProfile();

  /**
   * Handle language change
   */
  const handleLanguageChange = async (newLanguage: string) => {
    try {
      await updateUserSettings({ preferredLanguage: newLanguage as Language });
    } catch (error) {
      console.error('Failed to update language:', error);
      toast.error(t.settings.notifications.updateError);
    }
  };

  /**
   * Handle theme change
   */
  const handleThemeChange = async (newTheme: string) => {
    try {
      await updateUserSettings({ theme: newTheme as 'light' | 'dark' | 'system' });
    } catch (error) {
      console.error('Failed to update theme:', error);
      toast.error(t.settings.notifications.updateError);
    }
  };

  /**
   * Get current first name from cached profile, then token claims
   */
  const getCurrentFirstName = () => {
    // First priority: cached profile (optimistic update)
    if (userProfile?.givenName) {
      return userProfile.givenName;
    }

    // Second priority: token claims
    const accounts = instance.getAllAccounts();
    if (accounts.length > 0) {
      const currentAccount = accounts[0];
      if (currentAccount.idTokenClaims?.given_name) {
        return currentAccount.idTokenClaims.given_name as string;
      }
      if (currentAccount.name) {
        return currentAccount.name.split(' ')[0];
      }
    }
    return account.username.split('@')[0];
  };

  /**
   * Get current last name from cached profile, then token claims
   */
  const getCurrentLastName = () => {
    // First priority: cached profile (optimistic update)
    if (userProfile?.surname) {
      return userProfile.surname;
    }

    // Second priority: token claims
    const accounts = instance.getAllAccounts();
    if (accounts.length > 0) {
      const currentAccount = accounts[0];
      if (currentAccount.idTokenClaims?.family_name) {
        return currentAccount.idTokenClaims.family_name as string;
      }
      if (currentAccount.name) {
        const parts = currentAccount.name.split(' ');
        return parts.length > 1 ? parts[parts.length - 1] : '';
      }
    }
    return '';
  };

  /**
   * Handle form submission
   */
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    const newGivenName = firstName || getCurrentFirstName();
    const newSurname = lastName || getCurrentLastName();

    try {
      // Optimistically update the cache immediately for instant UI feedback
      updateUserProfile({
        givenName: newGivenName,
        surname: newSurname,
      });

      // Then make the API call to Azure AD
      await updateProfileMutation.mutateAsync({
        data: {
          givenName: newGivenName,
          surname: newSurname,
        },
      });

      toast.success(t.settings.notifications.updateSuccess);

      // Clear the form fields after successful save
      setFirstName('');
      setLastName('');
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error(t.settings.notifications.updateError);

      // Revert the optimistic update by clearing cache on error
      // This will make it fall back to token claims
      clearUserProfile();
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <PageHeader title={t.settings.title} description={t.settings.description} />

      <Card>
        <CardHeader>
          <CardTitle>{t.settings.profileInfo.title}</CardTitle>
          <CardDescription>
            {t.settings.profileInfo.description}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={(e) => { void handleSave(e); }} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">{t.settings.fields.firstName}</Label>
              <Input
                id="firstName"
                type="text"
                value={firstName || getCurrentFirstName()}
                onChange={(e) => { setFirstName(e.target.value); }}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="lastName">{t.settings.fields.lastName}</Label>
              <Input
                id="lastName"
                type="text"
                value={lastName || getCurrentLastName()}
                onChange={(e) => { setLastName(e.target.value); }}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">{t.settings.fields.email}</Label>
              <Input
                id="email"
                type="email"
                value={account.username}
                disabled
                className="bg-muted"
              />
              <p className="text-xs text-muted-foreground">{t.settings.fields.emailNote}</p>
            </div>

            <div className="flex justify-end pt-4">
              <LoadingButton type="submit" isLoading={updateProfileMutation.isPending}>
                {t.settings.actions.save}
              </LoadingButton>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t.settings.title}</CardTitle>
          <CardDescription>
            {t.settings.description}
          </CardDescription>
        </CardHeader>
        <CardContent className='flex flex-col gap-4'>
          <div className="space-y-2">
            <Label htmlFor="language">{t.settings.language.label}</Label>
            <p className="text-sm text-muted-foreground">
              {t.settings.language.description}
            </p>
            <Select value={language} onValueChange={(v) => { void handleLanguageChange(v); }} disabled={isUpdatingSettings}>
              <SelectTrigger id="language" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LANGUAGES.map((lang) => (
                  <SelectItem key={lang.code} value={lang.code}>
                    {lang.getLabel(t)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="theme">{t.settings.appearance.themeLabel}</Label>
            <p className="text-sm text-muted-foreground">
              {t.settings.appearance.themeDescription}
            </p>
            <Select value={theme} onValueChange={(v) => { void handleThemeChange(v); }} disabled={isUpdatingSettings}>
              <SelectTrigger id="theme" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="light">{t.settings.appearance.themeOptions.light}</SelectItem>
                <SelectItem value="dark">{t.settings.appearance.themeOptions.dark}</SelectItem>
                <SelectItem value="system">{t.settings.appearance.themeOptions.system}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <LegalDocumentsSettingsCard />

      <UserEmailNotificationSettingsCard />

      {isAdmin && <OrgSettingsCard />}

      {showFeatureFlags && <FeatureFlagsSettingsCard />}
    </div>
  );
}
